import { useState } from "react";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut
} from "firebase/auth";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { useMode } from "../context/ModeContext";
import { doc, setDoc } from "firebase/firestore";
import AuthRedirect from "../components/AuthRedirect";
import uiStyles from "../theme/uiStyles";

export default function Register() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const { setMode } = useMode();

  const register = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim()) {
      setError("First name, last name, email, and password are required.");
      return;
    }

    if (!acceptedTerms) {
      setError("You must agree to the Terms and Conditions.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      const user = userCredential.user;

      await sendEmailVerification(user);

      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        createdAt: new Date(),
        termsAcceptedAt: new Date(),
        cuisine: "",
        location: "",
        texture: ""
      });

      setMode("personal");
      await signOut(auth);

      nav("/login", {
        state: {
          message: `Verification email sent to ${user.email}. Please verify your email before logging in.`
        }
      });
    } catch (err) {
      setError(err.message || "Unable to register right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthRedirect>
      <div style={uiStyles.navbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/logo.png" alt="NextBite" style={{ height: 32, width: 32, objectFit: "contain" }} />
          <span style={{ fontWeight: 700, fontSize: 17, color: "#1a1a1a", letterSpacing: 0.2 }}>NextBite</span>
        </div>
        <button style={uiStyles.secondaryBtn} onClick={() => nav("/home")}>
          Home
        </button>
      </div>
      <div style={uiStyles.page}>
        <div style={{ ...uiStyles.card, padding: "28px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={{ textAlign: "center" }}>
            <h2 style={{ margin: "0 0 4px" }}>Create account</h2>
            <p style={{ margin: 0, fontSize: 14, color: "#64748b" }}>Save your preferences and meal history</p>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <input
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              style={{ ...uiStyles.input, marginBottom: 0, flex: 1 }}
            />
            <input
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              style={{ ...uiStyles.input, marginBottom: 0, flex: 1 }}
            />
          </div>

          <input
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ ...uiStyles.input, marginBottom: 0 }}
          />

          <input
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ ...uiStyles.input, marginBottom: 0 }}
          />

          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "#f25f4c", cursor: "pointer", flexShrink: 0 }}
            />
            <span style={{ fontSize: 14, color: "#475569" }}>
              I agree to the{" "}
              <button
                type="button"
                onClick={() => nav("/terms-and-conditions")}
                style={styles.inlineLink}
              >
                Terms and Conditions
              </button>
            </span>
          </label>

          {error && (
            <p style={{ margin: 0, color: "#dc2626", fontSize: 14, textAlign: "center" }}>
              {error}
            </p>
          )}

          <button onClick={register} style={uiStyles.primaryBtn} disabled={loading}>
            {loading ? "Sending verification..." : "Create account"}
          </button>

          <p style={{ margin: 0, textAlign: "center", fontSize: 14, color: "#6b7280" }}>
            Already have an account?{" "}
            <span
              onClick={() => nav("/login")}
              style={{ cursor: "pointer", color: "#f25f4c", fontWeight: 600 }}
            >
              Login
            </span>
          </p>
        </div>
      </div>
    </AuthRedirect>
  );
}

const styles = {
  checkboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    cursor: "pointer"
  },
  inlineLink: {
    border: "none",
    background: "transparent",
    padding: 0,
    color: "#f25f4c",
    cursor: "pointer",
    fontSize: 14,
    textDecoration: "underline",
    fontWeight: 600
  }
};
